// Workspace default last page: a static page appended after a card's own pages in the viewer.

type Stage = { width: number; height: number };

const PAGE_KEYS = ['images', 'textBoxes', 'ctas', 'gifs', 'rects', 'videos'];
const LENGTH_PROPS = ['width', 'height', 'fontSize', 'strokeWidth', 'cornerRadius', 'padding', 'radius'];

/** True when a page has at least one element (an enabled but never-designed last page is skipped). */
export const pageHasContent = (page?: any): boolean =>
	!!page && PAGE_KEYS.some((key) => Array.isArray(page[key]) && page[key].length > 0);

const blankPage = () => [{ images: [], textBoxes: [] }];

/**
 * Pads pages for the spread layout (at least 4, even count). When a last page is given it is always
 * the final page, with any blank padding placed before it.
 */
export const assemblePages = (pages: any[][], lastPage?: any): any[][] => {
	const out = [...pages];
	const extra = lastPage ? 1 : 0;
	while (out.length + extra < 4 || (out.length + extra) % 2 !== 0) out.push(blankPage());
	if (lastPage) out.push([lastPage]);
	return out;
};

/**
 * Maps a page designed at `from` onto a card stage `to`: uniform scale, centred (letterboxed).
 * The background image is stretched to fill the whole card.
 */
export const scalePage = (page: any, from: Stage, to: Stage): any => {
	if (!page || !from?.width || !from?.height || !to?.width || !to?.height) return page;
	if (from.width === to.width && from.height === to.height) return page;
	const s = Math.min(to.width / from.width, to.height / from.height);
	const dx = (to.width - from.width * s) / 2;
	const dy = (to.height - from.height * s) / 2;

	const scaleItem = (item: any) => {
		if (!item || typeof item !== 'object') return item;
		if (item.name === 'bg_image') return { ...item, x: 0, y: 0, width: to.width, height: to.height };
		const next = { ...item };
		if (typeof item.x === 'number') next.x = item.x * s + dx;
		if (typeof item.y === 'number') next.y = item.y * s + dy;
		LENGTH_PROPS.forEach((prop) => {
			if (typeof item[prop] === 'number') next[prop] = item[prop] * s;
		});
		return next;
	};

	const out = { ...page };
	PAGE_KEYS.forEach((key) => {
		if (Array.isArray(page[key])) out[key] = page[key].map(scaleItem);
	});
	return out;
};

export const blankLastPage = () => ({ images: [], textBoxes: [], ctas: [], gifs: [], rects: [], videos: [] });

/** Strips editor-only state from a page before saving it (same rules as the card editor's autosave). */
export const serializePageForSave = (page: any) => ({
	images: (page?.images || []).map((image: any) => {
		const { image: _htmlImage, personalizedUrl: _p, mergeTag: _m, ...rest } = image || {};
		return rest;
	}),
	textBoxes: (page?.textBoxes || []).filter((textBox: any) => textBox?.text !== 'Start typing'),
	ctas: page?.ctas || [],
	gifs: page?.gifs || [],
	rects: page?.rects || [],
	videos: (page?.videos || []).map((vid: any) => ({
		id: vid.id,
		name: vid.name,
		draggable: !!vid.draggable,
		src: vid.src,
		x: vid.x,
		y: vid.y,
		width: vid.width,
		height: vid.height,
	})),
});
